"use client";

import { useState, useEffect } from "react";
import Link from "next/link";

type ConsentState = "pending" | "accepted" | "rejected";

const COOKIE_CONSENT_KEY = "cookie-consent";

export function CookieConsent() {
  const [consent, setConsent] = useState<ConsentState | null>(null);

  useEffect(() => {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === "accepted" || stored === "rejected") {
      setConsent(stored);
    } else {
      setConsent("pending");
    }
  }, []);

  function accept() {
    localStorage.setItem(COOKIE_CONSENT_KEY, "accepted");
    setConsent("accepted");
  }

  function reject() {
    localStorage.setItem(COOKIE_CONSENT_KEY, "rejected");
    setConsent("rejected");
  }

  // Don't render anything until we've checked localStorage, or if user already chose
  if (consent !== "pending") return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-border/60 bg-card/95 backdrop-blur-md">
      <div className="mx-auto flex max-w-6xl flex-col gap-4 px-6 py-5 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex-1">
          <p className="text-sm text-foreground font-medium">We use cookies</p>
          <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
            We use essential cookies to make BuildMagic work. With your consent, we may also use
            non-essential cookies to improve your experience and analyze usage.{" "}
            <Link
              href="/cookie-policy"
              className="text-violet-400 underline underline-offset-2 hover:text-violet-300"
            >
              Cookie Policy
            </Link>
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <button
            onClick={reject}
            className="rounded-lg border border-border/60 px-4 py-2 text-sm font-medium text-muted-foreground transition-colors hover:border-border hover:text-foreground"
          >
            Reject all
          </button>
          <button
            onClick={accept}
            className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-zinc-950 transition-colors hover:bg-zinc-200"
          >
            Accept all
          </button>
        </div>
      </div>
    </div>
  );
}

/** Helper to check if the user has accepted cookies (for conditionally loading analytics). */
export function hasConsentedToCookies(): boolean {
  if (typeof window === "undefined") return false;
  return localStorage.getItem(COOKIE_CONSENT_KEY) === "accepted";
}
